import { GOOGLE_ADS_CONVERSION_ID, GOOGLE_ADS_LEAD_LABEL, pushEvent } from '../../utils/analytics';
import type { ChallengeExperimentContext } from './challengePageExperiment';

/** What this module hands the Google tag (gtag.js). */
type GoogleAdsUserData = { email?: string; phone_number?: string };
type GoogleAdsConversionParams = {
  send_to: string;
  value: number;
  currency: 'USD';
  transaction_id: string;
  transport_type?: 'beacon';
};

declare global {
  interface Window {
    __BOTSPOT_PLAYWRIGHT_QUALIFYING_IDLE_MS__?: number;
    gtag?: {
      (command: 'set', target: 'user_data', params: GoogleAdsUserData): void;
      (command: 'event', target: 'conversion', params: GoogleAdsConversionParams): void;
    };
  }
}

/**
 * The two tap questions asked right after a free challenge seat is saved, and
 * the value the one Google Ads Lead carries (Rob, 2026-09-30, revised
 * 2026-10-01). EVERY number lives in CHALLENGE_LEAD_VALUE_MODEL below; update
 * it there and nowhere else.
 *
 * Value = average course price ($2,000) x base buy rate (6.06%)
 *         x lift(how long investing) x lift(spent on courses), whole dollars.
 * A question not answered has lift 1.0 (the average). Each lift is the buy
 * rate of people who gave that answer divided by the question's base rate,
 * from old ActiveCampaign applicants 2020-2024. Sources:
 *   MarketingManager/docs/investigations/2026-09-30_challenge-page-test-plan.md
 *   /Users/robertgrzesik/Development/active_campaign_notes_extract/all.csv
 * Examples: nothing answered $121; Never + $0 $18; 10+ years + over $20,000
 * $670.
 *
 * Every seat sends exactly one Lead (transaction_id = registration id), at the
 * first of: both answered, the page closing while step 2 shows, or about 3
 * minutes idle on step 2. Tab switching alone never sends it. Node stores the
 * answers and the value sent (botspot_node challengeQualifyingAnswers.controller.ts)
 * and keeps no copy of these numbers.
 */

export const CHALLENGE_INVESTING_EXPERIENCE_OPTIONS = [
  { value: 'never', label: 'Never' },
  { value: '1_year_or_less', label: '1 year or less' },
  { value: '1_to_3_years', label: '1 to 3 years' },
  { value: '3_to_10_years', label: '3 to 10 years' },
  { value: '10_plus_years', label: '10+ years' },
] as const;

export const CHALLENGE_COURSE_SPEND_OPTIONS = [
  { value: '0', label: '$0' },
  { value: 'under_1000', label: 'Under $1,000' },
  { value: '1000_to_5000', label: '$1,000 to $5,000' },
  { value: '5000_to_20000', label: '$5,000 to $20,000' },
  { value: 'over_20000', label: 'Over $20,000' },
] as const;

export type ChallengeInvestingExperience =
  (typeof CHALLENGE_INVESTING_EXPERIENCE_OPTIONS)[number]['value'];
export type ChallengeCourseSpend = (typeof CHALLENGE_COURSE_SPEND_OPTIONS)[number]['value'];

export const CHALLENGE_LEAD_VALUE_MODEL = {
  averageCoursePriceUsd: 2000,
  baseBuyRate: 0.0606,
  investingExperienceLift: {
    never: 0.33,
    '1_year_or_less': 0.52,
    '1_to_3_years': 1.53,
    '3_to_10_years': 1.41,
    '10_plus_years': 2.82,
  },
  courseSpendLift: {
    '0': 0.45,
    under_1000: 0.49,
    '1000_to_5000': 1.53,
    '5000_to_20000': 1.82,
    over_20000: 1.96,
  },
  // Step 2 left untouched this long sends the Lead with the answers so far.
  idleMs: 180_000,
} as const satisfies {
  averageCoursePriceUsd: number;
  baseBuyRate: number;
  investingExperienceLift: Record<ChallengeInvestingExperience, number>;
  courseSpendLift: Record<ChallengeCourseSpend, number>;
  idleMs: number;
};

const INVESTING_VALUES: ReadonlySet<string> = new Set(
  CHALLENGE_INVESTING_EXPERIENCE_OPTIONS.map((option) => option.value),
);
const SPEND_VALUES: ReadonlySet<string> = new Set(
  CHALLENGE_COURSE_SPEND_OPTIONS.map((option) => option.value),
);

export const isChallengeInvestingExperience = (
  value: string | null | undefined,
): value is ChallengeInvestingExperience => Boolean(value) && INVESTING_VALUES.has(String(value));

export const isChallengeCourseSpend = (
  value: string | null | undefined,
): value is ChallengeCourseSpend => Boolean(value) && SPEND_VALUES.has(String(value));

export type ChallengeQualifyingAnswers = {
  investingExperience?: string | null;
  courseSpend?: string | null;
};

/** Whole US dollars. A missing or unknown answer counts as average (lift 1). */
export function challengeQualifyingLeadValue({
  investingExperience,
  courseSpend,
}: ChallengeQualifyingAnswers): number {
  const model = CHALLENGE_LEAD_VALUE_MODEL;
  const investingLift = isChallengeInvestingExperience(investingExperience)
    ? model.investingExperienceLift[investingExperience]
    : 1;
  const spendLift = isChallengeCourseSpend(courseSpend) ? model.courseSpendLift[courseSpend] : 1;
  return Math.round(model.averageCoursePriceUsd * model.baseBuyRate * investingLift * spendLift);
}

/**
 * The idle wait. Playwright may shorten it with a positive
 * window.__BOTSPOT_PLAYWRIGHT_QUALIFYING_IDLE_MS__ so the idle path is proven
 * without a 3-minute wait; everyone else gets the model's value.
 */
export function challengeQualifyingIdleMs(): number {
  const override = Number(window.__BOTSPOT_PLAYWRIGHT_QUALIFYING_IDLE_MS__);
  return Number.isFinite(override) && override > 0 ? override : CHALLENGE_LEAD_VALUE_MODEL.idleMs;
}

/**
 * Google wants E.164 for enhanced conversions. Only the shapes the challenge
 * form accepts are converted; anything else is left out rather than guessed.
 */
export function toGoogleAdsPhoneNumber(phone: string | null | undefined): string | undefined {
  const raw = String(phone || '').trim();
  if (!raw) return undefined;
  const digits = raw.replace(/\D/g, '');
  if (raw.startsWith('+')) return /^[1-9][0-9]{7,14}$/.test(digits) ? `+${digits}` : undefined;
  if (/^[2-9][0-9]{9}$/.test(digits)) return `+1${digits}`;
  if (/^1[2-9][0-9]{9}$/.test(digits)) return `+${digits}`;
  return undefined;
}

const SENT_KEY_PREFIX = 'botspot.challengeQualifiedLeadSent.';
const sentThisPage = new Set<string>();

type Disarm = () => void;
const armedSeats = new Map<string, Disarm>();

/** Test hook: forget in-memory sends and disarm every pending sender. */
export const resetChallengeQualifiedLeadMemory = (): void => {
  sentThisPage.clear();
  [...armedSeats.values()].forEach((disarm) => disarm());
  armedSeats.clear();
};

/**
 * Arms the page-close and idle senders for one seat, once. `onTrigger` runs at
 * most once, with 'pagehide' (the page is going away) or 'idle' (step 2 left
 * untouched for challengeQualifyingIdleMs). Tab switching is not a trigger.
 * They are not torn down when step 2 unmounts, so a visitor who moves on
 * inside the app still sends the Lead when they close the tab or go idle.
 */
export function armChallengeQualifiedLead(
  registrationId: string,
  onTrigger: (trigger: 'pagehide' | 'idle') => void,
): void {
  if (!registrationId || armedSeats.has(registrationId)) return;
  let fired = false;
  const fire = (trigger: 'pagehide' | 'idle') => {
    if (fired) return;
    fired = true;
    disarm();
    onTrigger(trigger);
  };
  const onPageHide = () => fire('pagehide');
  const timer = window.setTimeout(() => fire('idle'), challengeQualifyingIdleMs());
  const disarm = () => {
    fired = true;
    window.removeEventListener('pagehide', onPageHide);
    window.clearTimeout(timer);
  };
  window.addEventListener('pagehide', onPageHide);
  armedSeats.set(registrationId, disarm);
}

/** Stops the page-close and idle senders once the Lead has gone out. */
export function disarmChallengeQualifiedLead(registrationId: string): void {
  armedSeats.get(registrationId)?.();
}

const alreadySent = (registrationId: string): boolean => {
  if (sentThisPage.has(registrationId)) return true;
  try {
    return window.sessionStorage.getItem(SENT_KEY_PREFIX + registrationId) === '1';
  } catch {
    return false;
  }
};

const rememberSent = (registrationId: string): void => {
  sentThisPage.add(registrationId);
  try {
    window.sessionStorage.setItem(SENT_KEY_PREFIX + registrationId, '1');
  } catch {
    // In-memory memory still stops a double send on this page.
  }
};

/** What sent the one Lead. Tab switching alone is never a trigger. */
export type ChallengeQualifiedLeadTrigger = 'answered' | 'pagehide' | 'idle' | 'left';

export type ChallengeQualifiedLeadInput = ChallengeQualifyingAnswers & {
  registrationId: string;
  trigger: ChallengeQualifiedLeadTrigger;
  email?: string | null;
  phone?: string | null;
};

export type ChallengeQualifiedLeadResult = { fired: boolean; value: number };

/**
 * The one Google Ads Lead for a free challenge seat, with the answers so far
 * (a missing answer counts as average). Once per registration: remembered in
 * memory and sessionStorage, and transaction_id = registration id so Google
 * also dedupes. A page that is closing sends it by beacon so it survives.
 *
 * The seat form's email and phone are gone from the page by now, so they are
 * handed to the Google tag as user data right before the conversion. gtag
 * normalizes and SHA-256 hashes them in the browser (enhanced conversions);
 * the plain values never leave in the request.
 */
export function sendChallengeQualifiedLeadConversion(
  input: ChallengeQualifiedLeadInput,
): ChallengeQualifiedLeadResult {
  const { registrationId, trigger } = input;
  const value = challengeQualifyingLeadValue(input);
  const notSent: ChallengeQualifiedLeadResult = { fired: false, value };
  if (!registrationId) return notSent;
  const { gtag } = window;
  if (!gtag || !GOOGLE_ADS_CONVERSION_ID || !GOOGLE_ADS_LEAD_LABEL) return notSent;
  if (alreadySent(registrationId)) return notSent;
  rememberSent(registrationId);

  const userData: GoogleAdsUserData = {};
  const email = String(input.email || '')
    .trim()
    .toLowerCase();
  if (email) userData.email = email;
  const phoneNumber = toGoogleAdsPhoneNumber(input.phone);
  if (phoneNumber) userData.phone_number = phoneNumber;
  if (userData.email || userData.phone_number) gtag('set', 'user_data', userData);
  const conversion: GoogleAdsConversionParams = {
    send_to: `${GOOGLE_ADS_CONVERSION_ID}/${GOOGLE_ADS_LEAD_LABEL}`,
    value,
    currency: 'USD',
    transaction_id: registrationId,
  };
  if (trigger === 'pagehide') conversion.transport_type = 'beacon';
  gtag('event', 'conversion', conversion);
  return { fired: true, value };
}

/**
 * Internal analytics for every seat, sent alongside the one Lead: PostHog
 * receives `challenge_qualifying_answered` with the answers so far, the value
 * and what triggered it, plus the experiment arm.
 */
export function trackChallengeQualifyingAnswered({
  investingExperience,
  courseSpend,
  value,
  trigger,
  googleAdsLeadSent,
  experimentContext,
}: ChallengeQualifyingAnswers & {
  value: number;
  trigger: ChallengeQualifiedLeadTrigger;
  googleAdsLeadSent: boolean;
  experimentContext?: ChallengeExperimentContext;
}): void {
  pushEvent({
    ...experimentContext,
    event: 'challenge_qualifying_answered',
    source: window.location.pathname,
    investing_experience: isChallengeInvestingExperience(investingExperience)
      ? investingExperience
      : 'not_answered',
    course_spend: isChallengeCourseSpend(courseSpend) ? courseSpend : 'not_answered',
    lead_value: value,
    lead_value_currency: 'USD',
    lead_trigger: trigger,
    google_ads_lead_sent: googleAdsLeadSent,
  });
}
